<template>
  <b-container v-if="canCreateRecurringPaymentRequest">
    <b-form @submit.prevent="onSubmit" @reset.prevent="reset">
      <PROrderInfo
        :errorMessages="errorMessages.orderInformation"
        v-model="$v.form.orderInformation.$model"
      />

      <ROrderDetails
        :errorMessages="errorMessages.orderDetails"
        :size="orderDetailsSize"
        :currencies="enabledCurrencyList"
        v-model="$v.form.orderDetails.$model"
      />
      <RecurringInfo
        :executionDateEnabled.sync="executionDateEnabled"
        :indefinitePeriod.sync="indefinitePeriod"
        :errorMessages="errorMessages.recurringInformation"
        v-model="$v.form.recurringInformation.$model"
      />

      <div class="d-flex justify-content-center">
        <b-button id="create-request" type="submit" variant="danger" size="lg"
          >Create Request</b-button
        >
      </div>
    </b-form>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </b-container>
</template>

<script>
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import PROrderInfo from "@/components/paymentrequest/PROrderInfo.vue";
import ROrderDetails from "@/components/paymentrequest/recurring/RecurringOrderDetails.vue";
import CurrencyMixin from "@/components/payment/CurrencyMixin.js";
import RecurringInfo from "@/components/paymentrequest/recurring/RecurringInfo.vue";
import RPRValidationMixin from "@/components/paymentrequest/RecurringPaymentRequestValidationMixin.js";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";
import { dateToAWSDate, parseToFloat } from "@/services/Formatting.js";
import {
  getDomain,
  getDefaultReceiptUrl
} from "@/services/MerchantSiteInteractionService.js";
import { API, graphqlOperation } from "aws-amplify";
import PaymentRequestPerimissionsMixin from "@/components/paymentrequest/PaymentRequestPermissionsMixin.js";

const AlertModal = AlertModalMixin.AlertModal;
const LoadingModal = AlertModalMixin.LoadingModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [
    RPRValidationMixin,
    AlertModalMixin,
    PaymentRequestPerimissionsMixin,
    CurrencyMixin
  ],
  components: {
    PROrderInfo,
    ROrderDetails,
    RecurringInfo
  },
  data() {
    return {
      executionDateEnabled: false,
      indefinitePeriod: false,
      form: {
        orderInformation: {
          firstName: "",
          lastName: "",
          email: "",
          orderNumber: ""
        },
        orderDetails: {
          amount: 0,
          currency: "",
          initialPaymentAmount: 0
        },
        recurringInformation: {
          period: 1,
          frequency: "",
          sendDate: this.setDefaultDate(0),
          executionDate: this.setDefaultDate(7),
          expiryDate: this.setDefaultDate(7),
          reason: ""
        }
      },
      lists: {
        paymentFrequencies: [
          { value: "Daily", text: "Daily" },
          { value: "Weekly", text: "Weekly" },
          { value: "Fortnightly", text: "Fortnightly/Bi-Weekly" },
          { value: "Monthly", text: "Monthly" },
          { value: "BiMonthly", text: "Bi-Monthly" },
          { value: "Quarterly", text: "Quarterly" },
          { value: "Yearly", text: "Yearly" }
        ]
      },
      orderDetailsSize: {
        currency_sm: 5,
        currencyAmount_sm: 7
      }
    };
  },
  computed: {
    currencyText() {
      return this.getCurrencyTextFromCode(this.form.orderDetails.currency);
    }
  },
  methods: {
    async onSubmit(e) {
      e.preventDefault();
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.alert("Form invalid", AlertModal.WARNING);
        return;
      }

      const confirm = await this.confirm(
        `Create Request?`,
        `Are you sure you want to?`,
        ConfirmationModal.INFORMATION
      );
      if (!confirm) return;
      var alertMessage = "Recurring Transaction Requested";
      var alertStatus = AlertModal.SUCCESS;
      try {
        this.startLoadingDialog("Initiating Recurring Request");
        const response = await this.createRecurringPaymentRequest(this.form);
        if (response) {
          this.reset();
        } else {
          alertMessage = "Failed to Request Recurring Transaction";
          alertStatus = AlertModal.ERROR;
        }
      } catch (err) {
        this.$logger.error(err);
        alertMessage =
          "An error occured when attempting to request Recurring Transaction";
        alertStatus = AlertModal.ERROR;
      }
      this.stopLoadingDialog(alertMessage, alertStatus);
    },
    reset() {
      this.executionDateEnabled = false;
      this.indefinitePeriod = false;
      this.form.orderInformation = {
        firstName: "",
        lastName: "",
        email: "",
        orderNumber: ""
      };
      this.form.orderDetails = {
        amount: 0,
        currency: this.defaultCurrencyCode,
        initialPaymentAmount: 0
      };
      this.form.recurringInformation = {
        period: 1,
        frequency: "",
        sendDate: this.setDefaultDate(0),
        executionDate: this.setDefaultDate(7),
        expiryDate: this.setDefaultDate(7),
        reason: ""
      };
    },
    setDefaultDate(daysInTheFuture) {
      const d = new Date();
      d.setDate(d.getDate() + daysInTheFuture);
      return d;
    },
    getCurrencyTextFromCode(code) {
      const currencyIndex = this.enabledCurrencyList.findIndex(
        c => c.value == code
      );
      return currencyIndex > -1
        ? this.enabledCurrencyList[currencyIndex].text
        : "";
    },
    async createRecurringPaymentRequest(form) {
      const createRecurringPaymentRequest = `
            mutation CreateRecurringPaymentRequest(
              $body: MutationCreateRecurringPaymentRequestInput!
            ) {
              createRecurringPaymentRequest(body: $body) {
                success
                messageId
                errorMessage
              }
            }
          `;

      const tenantAlias = await this.getMerchantId();
      const domain = getDomain();
      const returnUrl = "";
      const receiptUrl = getDefaultReceiptUrl();

      const recurringPaymentRequest = {
        tenantAlias,
        domain,
        returnUrl,
        receiptUrl,
        orderInformation: {
          firstName: form.orderInformation.firstName,
          lastName: form.orderInformation.lastName,
          email: form.orderInformation.email,
          orderNumber: form.orderInformation.orderNumber
        },
        orderDetails: {
          amount: parseToFloat(form.orderDetails.amount),
          currencyCode: form.orderDetails.currency,
          currencyText: this.currencyText,
          initialPaymentAmount: parseToFloat(
            form.orderDetails.initialPaymentAmount
          )
        },
        recurringInformation: {
          period: form.recurringInformation.period,
          frequency: form.recurringInformation.frequency,
          sendDate: dateToAWSDate(form.recurringInformation.sendDate),
          expiryDate: dateToAWSDate(form.recurringInformation.expiryDate),
          reason: form.recurringInformation.reason
        }
      };

      if (this.executionDateEnabled) {
        recurringPaymentRequest.recurringInformation.executionDate = dateToAWSDate(
          form.recurringInformation.executionDate
        );
      }

      if (this.indefinitePeriod) {
        recurringPaymentRequest.recurringInformation.period = -1;
      }

      try {
        const response = await API.graphql(
          graphqlOperation(createRecurringPaymentRequest, {
            body: recurringPaymentRequest
          })
        );

        const success = response.data.createRecurringPaymentRequest.success;

        if (!success) {
          const errorMessage =
            response.data.createRecurringPaymentRequest.errorMessage;
          throw new Error(errorMessage);
        }

        const messageId = response.data.createRecurringPaymentRequest.messageId;

        if (!messageId)
          throw new Error("Successful response with no message id");
        return true;
      } catch (e) {
        this.$logger.error(e);
      }
      return false;
    },
    swap() {
      const currency = document.getElementById("currency_select");
      const amount = document.getElementById("currency_input");
      const parent = document.getElementById("currency_parent");

      parent.insertBefore(amount, currency);
    }
  },
  async mounted() {
    await this.retrieveEnabledCurrenciesOnly();

    this.swap();
  }
};
</script>

<style lang="scss" scoped></style>
